// DevHub: how a search result READS — its kind, its display label, and its snippet.
//
// Extracted so the grouped list (home hero + Cmd+K) and the flat /search page derive a row's label
// from ONE place. They used to derive it independently, which is how a hit could read as
// "GitHub — Source Control" (a DOC) on the hero while landing on a section anchor inside that page,
// and read as "RenovateBot | devhub-docs docs" on /search. A row must say where it goes.
import type { ReactNode } from 'react';
import type { SearchResult } from '@backstage/plugin-search-common';

// TechDocs results are indexed as 'techdocs'; catalog entities as 'software-catalog'.
export const DOCS_TYPE = 'techdocs';

// A result is a catalog entity, the doc as a whole (its TechDocs site), a whole page within that doc,
// or a section (a heading within a page — its location carries a #fragment). `doc` is a display-tier
// distinction the grouped list applies to its tier-1 row; `classifyResult` never returns it.
export type ResultKind = 'entity' | 'doc' | 'page' | 'section';

export function classifyResult(result: SearchResult): ResultKind {
  if (result.type !== DOCS_TYPE) return 'entity';
  return (result.document.location || '').includes('#') ? 'section' : 'page';
}

/** A TechDocs result pointing at the doc's ROOT/landing page — /docs/<ns>/<kind>/<name> with no
 *  deeper path segment and no #section. This is the natural "the doc as a whole" row; deeper pages
 *  and section hits are the doc's content.
 *
 *  An anchored location is a section OF the landing page, never the page itself. This used to strip
 *  the #fragment before matching, so `/docs/default/component/github/#renovatebot` passed as the doc
 *  root — got promoted to the tier-1 DOC row, was relabelled "GitHub — Source Control" and badged
 *  DOC, and still navigated to the anchor. That is the mismatch users reported. */
const DOC_ROOT_LOCATION = /\/docs\/[^/]+\/[^/]+\/[^/?#]+\/?$/i;
export function isDocRoot(result: SearchResult): boolean {
  if (result.type !== DOCS_TYPE) return false;
  const raw = result.document.location || '';
  if (raw.includes('#')) return false;
  return DOC_ROOT_LOCATION.test(raw.split('?')[0]);
}

// TechDocs results carry the owning doc's identity (entityTitle = human name). Runtime fields not on
// the narrow base document type, so widen it here.
type DocWithEntity = SearchResult['document'] & {
  entityTitle?: string;
  name?: string;
};

/** Last resort when an entity has no `metadata.title`: the search document's `name` is the lowercased
 *  entity slug, and showing "devhub-docs" as a heading reads as a bug. Turn separators into spaces and
 *  capitalise, but only when the slug is entirely lowercase, so an already-cased name is never
 *  mangled. The authoritative fix is for the entity to declare `metadata.title`. */
function humanizeSlug(name: string): string {
  const spaced = name.replace(/[-_]+/g, ' ').trim();
  if (spaced !== spaced.toLowerCase()) return spaced;
  return spaced.replace(/\b\p{Ll}/gu, c => c.toUpperCase());
}

/** The doc a techdocs result belongs to — its human title, falling back to the entity name. */
export function docNameOf(result: SearchResult): string {
  const d = result.document as DocWithEntity;
  if (d.entityTitle) return d.entityTitle;
  return d.name ? humanizeSlug(d.name) : '';
}

/** Separates a doc name from the heading within it: "GitHub — Source Control › RenovateBot". */
export const LABEL_SEPARATOR = ' › ';

/**
 * What a row should READ as. A section hit is prefixed with the doc it lives in, so the label matches
 * the anchor it navigates to. Everything else reads as its own title. Callers that already name the
 * doc elsewhere (the grouped list's group header) should use `document.title` directly instead.
 */
export function resultLabel(result: SearchResult): string {
  const title = result.document.title || '';
  if (classifyResult(result) !== 'section') return title;
  const doc = docNameOf(result);
  return doc && doc !== title ? `${doc}${LABEL_SEPARATOR}${title}` : title;
}

/**
 * The result's snippet, showing WHY it matched: the lexical arm's `ts_headline` context with the
 * matched terms wrapped in <mark>. The backend emits per-query marker tags (see lexicalQuery.ts) for
 * exactly this; before this existed the UI rendered the head of `document.text` instead, so a row
 * about RenovateBot could sit under a title that never mentions it. Semantic/fuzzy-only hits carry no
 * highlight, so they fall back to the document text.
 */
export function ResultSnippet({ result }: { result: SearchResult }) {
  const { highlight } = result;
  const marked = highlight?.fields?.text;
  const { preTag, postTag } = highlight ?? {};

  if (!marked || !preTag || !postTag) {
    const text = result.document.text;
    return text ? <span className="devhub-sr__item-snippet">{text}</span> : null;
  }

  // Split the markers back out into <mark> elements. Rendered as text nodes, so document content
  // that happens to look like markup stays inert.
  const pieces: ReactNode[] = [];
  marked.split(preTag).forEach((chunk, i) => {
    if (i === 0) {
      if (chunk) pieces.push(chunk);
      return;
    }
    const [match, ...rest] = chunk.split(postTag);
    if (match) {
      pieces.push(
        <mark key={`m${i}`} className="devhub-sr__item-mark">
          {match}
        </mark>,
      );
    }
    const tail = rest.join(postTag);
    if (tail) pieces.push(tail);
  });

  if (!pieces.length) return null;
  return <span className="devhub-sr__item-snippet">{pieces}</span>;
}
